/* eslint-disable max-len */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { sortBy } from 'lodash'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { compile } from '../../../utils/search'

interface Member {
  id: number
  name: string
  kana: string | null
  email: string
  code: string | null
  profile_image: string | null
}

interface Team {
  id: number
  parent_team_id: number | null
  name: string
  code: string | null
  managers: Member[]
  members: Member[]
}

interface Props {
  selectMemberIdList: number[]
  selectTeamList: {id: number, scope: string, include_nested_teams: boolean | null}[]
  callback: (data: {members: { id: number, name: string }[], teams: { id: number, name: string, scope: string, include_nested_teams: boolean | null }[]}) => void
  memberList: { id: number, name: string, kana: string | null, profile_image: string | null, active: boolean }[]
  teamList: Team[]
  defaultSelectScope: string
  isClear?: boolean
  isRealTime?: boolean
  blockedMemberIdList?: number[]
  displayType?: string
}

const DISPLAY_LABEL: Record<string, string> = {
  all: "チーム・ユーザー",
  team: "チーム",
  member: "ユーザー",
}

const AddTeamAndMemberModule: FC<Props> = (props) => {
  const {
    selectMemberIdList,
    selectTeamList,
    callback,
    memberList,
    teamList,
    defaultSelectScope,
    isClear,
    isRealTime,
    blockedMemberIdList,
    displayType,
  } = props

  const modalRef = useRef<HTMLDivElement>(null)
  const [isSearchDisplay, setIsSearchDisplay] = useState(false)
  const [selectMembers, setSelectMembers] = useState<{id: number, name: string}[]>([])
  const [selectTeams, setSelectTeams] = useState<{id: number, name: string, scope: string, include_nested_teams: boolean | null}[]>([])

  const [members, setMembers] = useState<{id: number, name: string, kana: string | null, profile_image: string | null, active: boolean}[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [searchTeamWord, setSearchTeamWord] = useState<string>("")
  const [searchMemberWord, setSearchMemberWord] = useState<string>("")

  const [hoverTeams, setHoverTeams] = useState<{id: number, y: number}[]>([])

  const [isSelectScope, setIsSelectScope] = useState(false)
  const [isSelectManager, setIsSelectManager] = useState(false)
  const [isSelectMember, setIsSelectMember] = useState(false)
  const [isSelectAllMember, setIsSelectAllMember] = useState(true)
  const [selectTeam, setSelectTeam] = useState<Team>()

  const membersInit = useMemo(() => (
    memberList || []
  ), [memberList])

  const teamsInit = useMemo(() => (
    teamList || []
  ), [teamList])

  const changeDefaultSelectScope = (scope: string) => {
    if (scope === "both") {
      setIsSelectManager(true)
      setIsSelectMember(true)
    } else if (scope === "manager") {
      setIsSelectManager(true)
      setIsSelectMember(false)
    } else if (scope === "member") {
      setIsSelectManager(false)
      setIsSelectMember(true)
    }
  }

  const getParentTeamNameList = (id: number | null): string[] => {
    if (id === null) return []
    const targetTeam = teamList.find((v) => v.id === id)
    if (!targetTeam || !targetTeam.parent_team_id) return []
    return [targetTeam.name, ...getParentTeamNameList(targetTeam.parent_team_id)]
  }

  const buildFullPathName = (id: number) => getParentTeamNameList(id).reverse().join(" > ")

  const getAllMemberList = (id: number | null): Member[] => {
    const tmpTeams = teamList.filter((v) => v.parent_team_id === id)
    if (!tmpTeams.length) return []
    const tmpMembers = [...tmpTeams.map((v) => [...v.managers, ...v.members]).flat(), ...tmpTeams.map((v) => getAllMemberList(v.id).flat()).flat()]
    return Array.from(new Map(tmpMembers.map((v) => [v.id, v])).values())
  }

  const clickMember = (memberId: number) => {
    let tmpMembers: {id: number, name: string}[] = []
    if (selectMembers.map((v) => v.id).includes(memberId)) {
      tmpMembers = selectMembers.filter((v) => v.id !== memberId)
    } else {
      tmpMembers = [...selectMembers, { id: memberId, name: members.find((v) => v.id === memberId)?.name || "" }]
    }
    setSelectMembers(tmpMembers)
    if (isRealTime) callback({ members: tmpMembers, teams: selectTeams })
  }

  const submitSelectTeam = (teamId: number) => {
    let tmpTeams: {id: number, name: string, scope: string, include_nested_teams: boolean | null}[] = []
    if (selectTeams.map((v) => v.id).includes(teamId)) {
      tmpTeams = selectTeams.filter((v) => v.id !== teamId)
    } else {
      let tmpScope = "member"
      let tmpIncludeNestedTeams = isSelectAllMember
      if (isSelectManager && isSelectMember) {
        tmpScope = "both"
      } else if (isSelectManager) {
        tmpScope = "manager"
        tmpIncludeNestedTeams = false
      }
      tmpTeams = [...selectTeams, { id: teamId, name: teams.find((v) => v.id === teamId)?.name || "", scope: tmpScope, include_nested_teams: tmpIncludeNestedTeams }]
      setIsSelectScope(false)
    }
    setSelectTeams(tmpTeams)
    if (isRealTime) callback({ members: selectMembers, teams: tmpTeams })
    setIsSelectAllMember(true)
    changeDefaultSelectScope(defaultSelectScope)
  }

  const selectTeamScope = (teamId: number) => {
    setIsSelectScope(true)
    setSelectTeam(teamList.find((v) => v.id === teamId))
  }

  const ejectTeamId = (teamId: number) => {
    const tmpTeams = selectTeams.filter((v) => v.id !== teamId)
    setSelectTeams(tmpTeams)
    if (isRealTime) callback({ members: selectMembers, teams: tmpTeams })
  }

  const searchTeam = (words: string) => {
    setSearchTeamWord(words)
    if (!words) {
      setTeams([...teamsInit])
      return
    }
    const word = words.toLowerCase().split(/\s+/)
    const list = teamsInit.filter((team) => {
      if (team === null) return false
      return compile(word).test(team.name.toLowerCase())
    })
    setTeams(sortBy(list, 'id'))
  }

  const searchMember = (words: string) => {
    setSearchMemberWord(words)
    if (!words) {
      setMembers([...membersInit])
      return
    }
    const word = words.toLowerCase().split(/\s+/)
    const list = membersInit.filter((member) => {
      if (member === null) return false
      return compile(word).test(member.name.toLowerCase() + member.kana?.toLocaleLowerCase())
    })
    setMembers(sortBy(list, 'id'))
  }

  const resetSearchTeam = () => {
    setSearchTeamWord("")
    setTeams([...teamsInit])
  }

  const resetSearchMember = () => {
    setSearchMemberWord("")
    setMembers([...membersInit])
  }

  const openSearch = () => {
    setIsSearchDisplay(true)
  }

  const closeSearch = () => {
    setIsSearchDisplay(false)
    resetSearchTeam()
    resetSearchMember()
    callback({ members: selectMembers, teams: selectTeams })
  }

  const closeSelectScope = () => {
    setIsSelectScope(false)
    setIsSelectAllMember(true)
    changeDefaultSelectScope(defaultSelectScope)
  }

  const hoverTeam = (teamId: number, element: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const rect = element.currentTarget.getBoundingClientRect()
    const modalRect = modalRef.current?.getBoundingClientRect()
    if (modalRect) {
      setHoverTeams((cur) => ([...cur, { id: teamId, y: (rect.top - modalRect.top) - 42 }]))
    }
  }

  const blurTeam = (teamId: number) => {
    setHoverTeams((cur) => cur.filter((w) => w.id !== teamId))
  }

  const reset = () => {
    setSelectMembers([])
    setSelectTeams([])
  }

  const buildTeamList = (level = 0, parent_id?: number | null, single?: boolean):JSX.Element => {
    let list: Team[] = []
    const levelCount = level + 1
    if (parent_id === 0) return (<></>)
    if (parent_id) {
      list = teamList.filter((v) => v.parent_team_id === parent_id)
    } else {
      list = teamList.filter((v) => v.parent_team_id === null)
    }
    return (
      <>
        {
          list.map((v, i) => (
            <React.Fragment key={`project_category_row_${v.id}`}>
              <div
                className="hover"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                  minHeight: level ? "30px" : "40px",
                  padding: "4px 8px",
                  border: level ? "none" : "solid 1px #CCCCCC",
                  width: level ? "200px" : "400px",
                  minWidth: level ? "200px" : "400px",
                }}
                onClick={(e) => {
                  if (selectTeams.find((w) => w.id === v.id)) {
                    ejectTeamId(v.id)
                  } else {
                    selectTeamScope(v.id)
                  }
                  e.stopPropagation()
                }}
                onMouseOver={(e) => hoverTeam(v.id, e)}
                onMouseLeave={() => blurTeam(v.id)}
                onFocus={() => { /**/ }}
                role="none"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {`${v.name}${!v.parent_team_id ? " (全社)" : ""}`}
                  {
                    !!selectTeams.find((w) => w.id === v.id) && (
                      <div
                        style={{
                          color: "#008800",
                          fontSize: "8px",
                          border: "solid 1px #008800",
                          borderRadius: "14px",
                          padding: "2px 4px",
                          backgroundColor: "#edffed",
                          whiteSpace: "nowrap",
                        }}
                      >
                        選択中
                      </div>
                    )
                  }
                </div>
                {
                  !single && !!teamList.find((w) => w.parent_team_id === v.id) && (
                    <div>
                      <FontAwesomeIcon icon={['fas', 'angle-right']} />
                    </div>
                  )
                }
                {
                  !single && !!teamList.find((w) => w.parent_team_id === v.id) && hoverTeams.map((w) => w.id).includes(v.id) && (
                    <>
                      <div
                        style={{
                          position: "absolute",
                          top: level ? `${i * 30}px` : `${hoverTeams.find((w) => w.id === v.id)?.y}px`,
                          right: "-180px",
                          display: "flex",
                          flexFlow: "column",
                          backgroundColor: "#FFFFFF",
                          border: "solid 1px #CCCCCC",
                          borderRadius: "4px",
                          padding: "8px",
                          zIndex: 2000,
                        }}
                        onClick={(e) => e.stopPropagation()}
                        role="none"
                      >
                        {buildTeamList(levelCount, v.id)}
                      </div>
                    </>
                  )
                }
              </div>
            </React.Fragment>
          ))
        }
      </>
    )
  }

  useEffect(() => {
    setMembers(memberList || [])
  }, [memberList])

  useEffect(() => {
    setTeams(teamList || [])
  }, [teamList])

  useEffect(() => {
    if (!memberList) return
    setSelectMembers(memberList.filter((v) => selectMemberIdList?.includes(v.id)))
  }, [selectMemberIdList, memberList])

  useEffect(() => {
    if (!teamList) return
    const tmpIds = selectTeamList.map((v) => v.id)
    setSelectTeams(
      teamList.filter((v) => tmpIds.includes(v.id)).map((v) => ({
        ...v,
        scope: selectTeamList.find((w) => w.id === v.id)?.scope || "both",
        include_nested_teams: selectTeamList.find((w) => w.id === v.id)?.include_nested_teams ?? null,
      })),
    )
  }, [selectTeamList, teamList])

  useEffect(() => {
    if (!isClear) return
    reset()
  }, [isClear])

  useEffect(() => {
    changeDefaultSelectScope(defaultSelectScope)
  }, [defaultSelectScope])

  return (
    <>
      <div
        className="add_button"
        onClick={() => openSearch()}
        role="none"
        ref={modalRef}
      >
        <FontAwesomeIcon icon={['fas', 'plus']} />
        &nbsp;
        追加
        <div className={`search_box ${isSearchDisplay || 'none'}`} style={{ left: "-10px" }}>
          <div
            style={{
              position: "relative",
            }}
          >
            <div className="search_detail" style={{ width: "400px" }}>
              <div className="category">
                <div className="category_title">
                  {`${DISPLAY_LABEL[displayType || "all"]}選択`}
                  {
                    !!selectMembers.length && (
                      <div
                        className="select_reset"
                        role="none"
                        onClick={reset}
                      >
                        リセット
                      </div>
                    )
                  }
                </div>
                {
                  ["all", "team"].includes(displayType || "all") && (
                    <>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          marginBottom: "8px",
                        }}
                      >
                        チーム
                      </div>
                      <div className="search_area">
                        <FontAwesomeIcon
                          icon={['fas', 'search']}
                          className="icon"
                        />
                        <input
                          type="text"
                          placeholder="チーム名を検索"
                          onChange={(event) => searchTeam(event.target.value)}
                          value={searchTeamWord}
                        />
                        {
                          searchTeamWord && (
                            <button type="button" className="reset" onClick={resetSearchTeam}>
                              <FontAwesomeIcon icon={['far', 'times-circle']} />
                            </button>
                          )
                        }
                        {
                          searchTeamWord && (
                            <div
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: "0",
                                border: "solid 1px #CCCCCC",
                                width: "100%",
                                height: "200px",
                                overflowY: "scroll",
                                backgroundColor: "#FFFFFF",
                                zIndex: "1221",
                                padding: "8px",
                              }}
                            >
                              {
                                teams.length ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexFlow: "column",
                                      gap: "4px",
                                    }}
                                  >
                                    {
                                      teams.map((v) => (
                                        <div
                                          className="hover"
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                            padding: "8px",
                                            whiteSpace: "nowrap",
                                          }}
                                          onClick={(e) => {
                                            if (selectTeams.find((w) => w.id === v.id)) {
                                              ejectTeamId(v.id)
                                            } else {
                                              selectTeamScope(v.id)
                                            }
                                            e.stopPropagation()
                                          }}
                                          role="none"
                                        >
                                          {buildFullPathName(v.id)}
                                          {
                                            !!selectTeams.find((w) => w.id === v.id) && (
                                              <div
                                                style={{
                                                  color: "#008800",
                                                  fontSize: "8px",
                                                  border: "solid 1px #008800",
                                                  borderRadius: "14px",
                                                  padding: "2px 4px",
                                                  backgroundColor: "#edffed",
                                                }}
                                              >
                                                選択中
                                              </div>
                                            )
                                          }
                                        </div>
                                      ))
                                    }
                                  </div>
                                ) : (
                                  <>
                                    {`「${searchTeamWord}」に該当するチーム名がありませんでした`}
                                  </>
                                )
                              }
                            </div>
                          )
                        }
                      </div>
                      <div
                        style={{
                          marginBottom: "16px",
                        }}
                      >
                        {buildTeamList(0, null, true)}
                        {buildTeamList(0, teamList.find((v) => v.parent_team_id === null)?.id)}
                      </div>
                    </>
                  )
                }
                {
                  ["all", "member"].includes(displayType || "all") && (
                    <>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          marginBottom: "16px",
                        }}
                      >
                        ユーザー
                      </div>
                      <div className="search_area">
                        <FontAwesomeIcon
                          icon={['fas', 'search']}
                          className="icon"
                        />
                        <input
                          type="text"
                          placeholder="ユーザーを検索"
                          onChange={(event) => searchMember(event.target.value)}
                          value={searchMemberWord}
                        />
                        {
                          searchMemberWord && (
                            <button type="button" className="reset" onClick={resetSearchMember}>
                              <FontAwesomeIcon icon={['far', 'times-circle']} />
                            </button>
                          )
                        }
                        {
                          searchMemberWord && (
                            <div
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: "0",
                                border: "solid 1px #CCCCCC",
                                width: "100%",
                                height: "200px",
                                overflowY: "scroll",
                                backgroundColor: "#FFFFFF",
                                zIndex: "1221",
                                padding: "8px",
                              }}
                            >
                              {
                                members.length ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: "4px",
                                    }}
                                  >
                                    {
                                      members.map((member) => (
                                        <>
                                          {
                                            blockedMemberIdList?.includes(member.id) ? (
                                              <div
                                                key={`member_${member.id}`}
                                                style={{
                                                  padding: "4px 8px",
                                                  backgroundColor: "#CCCCCC",
                                                  border: "solid 1px #CCCCCC",
                                                  color: "#333333",
                                                  borderRadius: "14px",
                                                  margin: 0,
                                                  whiteSpace: "nowrap",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  maxWidth: "100%",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "4px",
                                                  fontSize: "11px",
                                                }}
                                              >
                                                <img
                                                  alt="profile"
                                                  src={member.profile_image || `/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                                                  className="profile_icon"
                                                  style={{
                                                    borderRadius: "50%",
                                                    width: "20px",
                                                  }}
                                                />
                                                {member.name}
                                              </div>
                                            ) : (
                                              <button
                                                type="button"
                                                className={`chip ${selectMembers.map((v) => v.id).includes(member.id) ? "checked" : ""}`}
                                                key={`member_${member.id}`}
                                                onClick={() => clickMember(member.id)}
                                                style={{
                                                  padding: "4px 8px",
                                                }}
                                              >
                                                <img
                                                  alt="profile"
                                                  src={member.profile_image || `/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                                                  className="profile_icon"
                                                  style={{
                                                    borderRadius: "50%",
                                                    width: "20px",
                                                  }}
                                                />
                                                {member.name}
                                              </button>
                                            )
                                          }
                                        </>
                                      ))
                                    }
                                  </div>
                                ) : (
                                  <>
                                    {`「${searchMemberWord}」に該当するユーザーがいませんでした`}
                                  </>
                                )
                              }
                            </div>
                          )
                        }
                      </div>
                      <div
                        style={{
                          border: "solid 1px #CCCCCC",
                          padding: "4px",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                        }}
                      >
                        {
                          memberList.map((member) => (
                            <>
                              {
                                blockedMemberIdList?.includes(member.id) ? (
                                  <div
                                    key={`member_${member.id}`}
                                    style={{
                                      padding: "4px 8px",
                                      backgroundColor: "#CCCCCC",
                                      border: "solid 1px #CCCCCC",
                                      color: "#333333",
                                      borderRadius: "14px",
                                      margin: 0,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      fontSize: "11px",
                                    }}
                                  >
                                    <img
                                      alt="profile"
                                      src={member.profile_image || `/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                                      className="profile_icon"
                                      style={{
                                        borderRadius: "50%",
                                        width: "20px",
                                      }}
                                    />
                                    {member.name}
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    className={`chip ${selectMembers.map((v) => v.id).includes(member.id) ? "checked" : ""}`}
                                    key={`member_${member.id}`}
                                    onClick={() => clickMember(member.id)}
                                    style={{
                                      padding: "4px 8px",
                                      margin: 0,
                                    }}
                                  >
                                    <img
                                      alt="profile"
                                      src={member.profile_image || `/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                                      className="profile_icon"
                                      style={{
                                        borderRadius: "50%",
                                        width: "20px",
                                      }}
                                    />
                                    {member.name}
                                  </button>
                                )
                              }
                            </>
                          ))
                        }
                      </div>
                    </>
                  )
                }
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={`close_screen ${isSearchDisplay || 'none'}`} role="presentation" onClick={closeSearch} />
      {
        isSelectScope && (
          <>
            <div
              style={{
                position: "fixed",
                display: "flex",
                flexFlow: "column",
                gap: "16px",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "#FFFFFF",
                padding: "16px",
                borderRadius: "4px",
                zIndex: "2111",
                width: "500px",
              }}
            >
              <div
                style={{
                  textAlign: "center",
                  fontSize: "16px",
                }}
              >
                {selectTeam?.name}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "24px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    color: isSelectManager ? "#008800" : "#666666",
                  }}
                  onClick={() => setIsSelectManager((cur) => !cur)}
                  role="none"
                >
                  <FontAwesomeIcon
                    icon={['far', isSelectManager ? 'check-square' : 'square']}
                    style={{
                      fontSize: "16px",
                    }}
                  />
                  マネージャー
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    color: isSelectMember ? "#008800" : "#666666",
                  }}
                  onClick={() => setIsSelectMember((cur) => !cur)}
                  role="none"
                >
                  <FontAwesomeIcon
                    icon={['far', isSelectMember ? 'check-square' : 'square']}
                    style={{
                      fontSize: "16px",
                    }}
                  />
                  メンバー
                </div>
              </div>
              {
                isSelectMember && (
                  <div
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      padding: "8px",
                      fontSize: "10px",
                      display: "flex",
                      flexFlow: "column",
                      gap: "4px",
                      backgroundColor: "#F5F5F5",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                        color: !isSelectAllMember ? "#008800" : "#666666",
                      }}
                      onClick={() => setIsSelectAllMember(false)}
                      role="none"
                    >
                      <FontAwesomeIcon
                        icon={['far', !isSelectAllMember ? 'check-circle' : 'circle']}
                        style={{
                          fontSize: "12px",
                        }}
                      />
                      このチームのメンバー
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                        color: isSelectAllMember ? "#008800" : "#666666",
                      }}
                      onClick={() => setIsSelectAllMember(true)}
                      role="none"
                    >
                      <FontAwesomeIcon
                        icon={['far', isSelectAllMember ? 'check-circle' : 'circle']}
                        style={{
                          fontSize: "12px",
                        }}
                      />
                      このチームのメンバー + 配下チームに所属している全てのマネージャー・メンバー
                    </div>
                  </div>
                )
              }
              {
                (!isSelectManager && !isSelectMember) ? (
                  <>
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: "12px",
                        color: "#ff0000",
                      }}
                    >
                      マネージャーまたはメンバーは必ず選択してください。
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: "12px",
                      }}
                    >
                      {`以下のユーザーが所属する${selectTeam?.name}を追加します`}
                    </div>
                    <div
                      style={{
                        border: "dashed 2px #CCCCCC",
                        borderRadius: "4px",
                        padding: "12px 8px 8px",
                        width: "100%",
                        display: "flex",
                        flexFlow: "column",
                        gap: "16px",
                      }}
                    >
                      {
                        isSelectManager && (
                          <div
                            style={{
                              display: "flex",
                              gap: "4px",
                              flexWrap: "wrap",
                              position: "relative",
                              border: "solid 1px #CCCCCC",
                              borderRadius: "4px",
                              padding: "12px 8px 8px",
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                top: "0px",
                                transform: "translateY(-50%)",
                                fontSize: "10px",
                                backgroundColor: "#FFFFFF",
                                padding: "0 4px",
                              }}
                            >
                              マネージャー
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                                flexWrap: "wrap",
                                maxHeight: "300px",
                                overflowY: "scroll",
                                scrollbarWidth: "none",
                              }}
                            >
                              {
                                selectTeam?.managers.map((v) => (
                                  <div
                                    key={`member_${v.id}`}
                                    style={{
                                      border: "1px solid #CCCCCC",
                                      borderRadius: "20px",
                                      padding: "4px 8px",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      userSelect: "none",
                                      backgroundColor: "#FFFFFF",
                                    }}
                                  >
                                    <img
                                      alt=""
                                      src={v.profile_image || `/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                                      className="profile_icon"
                                      style={{
                                        borderRadius: "50%",
                                        width: "14px",
                                      }}
                                    />
                                    {v.name}
                                  </div>
                                ))
                              }
                              {
                                !selectTeam?.managers.length && (
                                  <>
                                    このチームにマネージャーは設定されていません
                                  </>
                                )
                              }
                            </div>
                          </div>
                        )
                      }
                      {
                        isSelectMember && !isSelectAllMember && (
                          <div
                            style={{
                              display: "flex",
                              gap: "4px",
                              flexWrap: "wrap",
                              position: "relative",
                              border: "solid 1px #CCCCCC",
                              borderRadius: "4px",
                              padding: "12px 8px 8px",
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                top: "0px",
                                transform: "translateY(-50%)",
                                fontSize: "10px",
                                backgroundColor: "#FFFFFF",
                                padding: "0 4px",
                              }}
                            >
                              このチームのメンバー
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                                flexWrap: "wrap",
                                maxHeight: "300px",
                                overflowY: "scroll",
                                scrollbarWidth: "none",
                              }}
                            >
                              {
                                selectTeam?.members.map((v) => (
                                  <div
                                    key={`member_${v.id}`}
                                    style={{
                                      border: "1px solid #CCCCCC",
                                      borderRadius: "20px",
                                      padding: "4px 8px",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      userSelect: "none",
                                      backgroundColor: "#FFFFFF",
                                    }}
                                  >
                                    <img
                                      alt=""
                                      src={v.profile_image || `/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                                      className="profile_icon"
                                      style={{
                                        borderRadius: "50%",
                                        width: "14px",
                                      }}
                                    />
                                    {v.name}
                                  </div>
                                ))
                              }
                              {
                                !selectTeam?.members.length && (
                                  <>
                                    このチームにメンバーは設定されていません
                                  </>
                                )
                              }
                            </div>
                          </div>
                        )
                      }
                      {
                        isSelectMember && isSelectAllMember && (
                          <div
                            style={{
                              display: "flex",
                              gap: "4px",
                              flexWrap: "wrap",
                              position: "relative",
                              border: "solid 1px #CCCCCC",
                              borderRadius: "4px",
                              padding: "12px 8px 8px",
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                top: "0px",
                                transform: "translateY(-50%)",
                                fontSize: "10px",
                                backgroundColor: "#FFFFFF",
                                padding: "0 4px",
                              }}
                            >
                              このチームのメンバー + 配下チームに所属している全てのマネージャー・メンバー
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                                flexWrap: "wrap",
                                maxHeight: "300px",
                                overflowY: "scroll",
                                scrollbarWidth: "none",
                              }}
                            >
                              {
                                Array.from(new Map([
                                  ...getAllMemberList(selectTeam?.id || null),
                                  ...teamList.find((v) => v.id === selectTeam?.id)?.members || [],
                                ].map((v) => [v.id, v])).values()).sort((a, b) => a.id - b.id).map((v) => (
                                  <div
                                    key={`member_${v.id}`}
                                    style={{
                                      border: "1px solid #CCCCCC",
                                      borderRadius: "20px",
                                      padding: "4px 8px",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      userSelect: "none",
                                      backgroundColor: "#FFFFFF",
                                    }}
                                  >
                                    <img
                                      alt=""
                                      src={v.profile_image || `/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                                      className="profile_icon"
                                      style={{
                                        borderRadius: "50%",
                                        width: "14px",
                                      }}
                                    />
                                    {v.name}
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        )
                      }
                    </div>
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: "12px",
                        color: "#ff0000",
                      }}
                    >
                      チームの参加者が変更になった際に、こちらの対象者も自動で変更されます。
                    </div>
                  </>
                )
              }
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "24px",
                }}
              >
                <button
                  type="button"
                  className="button cancel"
                  style={{
                    fontSize: "12px",
                    minWidth: "180px",
                    backgroundColor: "#CCCCCC",
                    border: "1px solid #CCCCCC",
                    borderRadius: "4px",
                    color: "#666666",
                    textAlign: "center",
                  }}
                  onClick={closeSelectScope}
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  className="button"
                  style={{
                    fontSize: "12px",
                    minWidth: "180px",
                    backgroundColor: "#4472BA",
                    border: "1px solid #4472BA",
                    borderRadius: "4px",
                    color: "#FFFFFF",
                    textAlign: "center",
                  }}
                  disabled={!isSelectManager && !isSelectMember}
                  onClick={() => submitSelectTeam(selectTeam?.id || 0)}
                >
                  追加する
                </button>
              </div>
            </div>
            <div className={`close_screen ${isSelectScope || 'none'} gray`} role="presentation" style={{ zIndex: "2110" }} onClick={closeSelectScope} />
          </>
        )
      }
    </>
  )
}

AddTeamAndMemberModule.defaultProps = {
  isClear: false,
  isRealTime: false,
  blockedMemberIdList: [],
  displayType: "all",
}

export default AddTeamAndMemberModule
